<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, helpers } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Kaufland issues a 32 character client key and a 64 character secret key, both lowercase hex.
const isClientKey = helpers.regex(/^[a-f0-9]{32}$/);
const isSecretKey = helpers.regex(/^[a-f0-9]{64}$/);

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      clientKey: '',
      secretKey: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    channelName: { required },
    clientKey: { required, isClientKey },
    secretKey: { required, isSecretKey },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const kauflandChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName,
            channel: {
              type: 'kaufland',
              client_key: this.clientKey.trim(),
              secret_key: this.secretKey.trim(),
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: kauflandChannel.id,
          },
        });
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.clientKey.$error }">
          {{ $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.LABEL') }}
          <input
            v-model="clientKey"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.PLACEHOLDER')
            "
            @blur="v$.clientKey.$touch"
          />
          <span v-if="v$.clientKey.$error" class="message">{{
            $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.ERROR')
          }}</span>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.CLIENT_KEY.SUBTITLE') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.secretKey.$error }">
          {{ $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.LABEL') }}
          <input
            v-model="secretKey"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.PLACEHOLDER')
            "
            @blur="v$.secretKey.$touch"
          />
          <span v-if="v$.secretKey.$error" class="message">{{
            $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.ERROR')
          }}</span>
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SECRET_KEY.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.KAUFLAND_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
